import { useEffect, useState, useCallback } from 'react';
import api from '../../api/axios';
import { toast } from 'react-toastify';
import BulkMaterialUpload from './BulkMaterialUpload';
import { isMaterialNumberRequired, MATERIAL_NUMBER_TYPES } from './materialFieldConfig';

const GRADIENT = 'linear-gradient(135deg,#D95A1A,#e87a3a)';

const EMPTY_DATA = { material_description: '', material_type: '', material_group: '', base_uom: '', material_number: '' };

const SearchSelect = ({ value, options, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const label = value
    ? (() => { const o = options.find(x => x.code === value); return o ? `${o.code}${o.description ? ' \u2013 ' + o.description : ''}` : value; })()
    : '';
  const filtered = options.filter(o => {
    const s = q.trim().toUpperCase();
    return !s || o.code.toUpperCase().includes(s) || (o.description || '').toUpperCase().includes(s);
  });
  return (
    <div style={{ position: 'relative' }}>
      <div style={SS.wrap}>
        <input style={SS.input} value={open ? q : label}
          placeholder={placeholder || 'Search\u2026'}
          onFocus={() => { setOpen(true); setQ(''); }}
          onChange={e => { setQ(e.target.value); setOpen(true); }} />
        {value && <button type="button" style={SS.clear} onClick={() => { onChange(''); setOpen(false); }}>&#x2715;</button>}
        <span style={SS.arrow} onMouseDown={() => setOpen(o => !o)}>&or;</span>
      </div>
      {open && (
        <div style={SS.drop} onMouseLeave={() => setOpen(false)}>
          {filtered.length === 0
            ? <div style={SS.none}>No match</div>
            : filtered.map(o => (
              <div key={o.code} style={{ ...SS.opt, ...(o.code === value ? SS.optA : {}) }}
                onMouseDown={() => { onChange(o.code); setOpen(false); setQ(''); }}>
                <span style={SS.code}>{o.code}</span>
                {o.description && <span style={SS.desc}>{o.description}</span>}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

const SS = {
  wrap: { position: 'relative', display: 'flex', alignItems: 'center' },
  input: { width: '100%', padding: '10px 52px 10px 13px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, color: '#0B1554', background: '#f8faff', boxSizing: 'border-box', outline: 'none' },
  clear: { position: 'absolute', right: 28, background: 'none', border: 'none', cursor: 'pointer', color: '#8892b0', fontSize: 12, padding: '0 4px' },
  arrow: { position: 'absolute', right: 10, cursor: 'pointer', color: '#8892b0', fontSize: 12, userSelect: 'none' },
  drop: { position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 8, boxShadow: '0 8px 24px rgba(11,21,84,0.12)', maxHeight: 220, overflowY: 'auto', marginTop: 4 },
  opt: { display: 'flex', alignItems: 'baseline', gap: 8, padding: '9px 13px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f0f2f8' },
  optA: { background: '#f0f4ff' },
  code: { fontWeight: 700, color: '#0B1554', minWidth: 48, flexShrink: 0 },
  desc: { color: '#4a5568', fontSize: 12.5 },
  none: { padding: '10px 13px', fontSize: 13, color: '#8892b0' },
};

const MaterialRequestForm = () => {
  const [mode, setMode] = useState('single');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [data, setData] = useState({ ...EMPTY_DATA });
  const [loading, setLoading] = useState(false);
  const [dupResult, setDupResult] = useState(null);
  const [dupChecking, setDupChecking] = useState(false);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [materialGroups, setMaterialGroups] = useState([]);
  const [uoms, setUoms] = useState([]);

  useEffect(() => {
    api.get('/sap/options/material-types').then(r => setMaterialTypes(r.data)).catch(() => {});
    api.get('/sap/options/material-group').then(r => setMaterialGroups(r.data)).catch(() => {});
    api.get('/sap/options/uom').then(r => setUoms(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!data.material_description.trim()) { setDupResult(null); return; }
    setDupResult(null);
    setDupChecking(true);
    const t = setTimeout(async () => {
      try {
        const { data: res } = await api.post('/sap/check-duplicate', {
          description: data.material_description.trim(),
          uom: data.base_uom || '',
        });
        setDupResult(res);
      } catch { toast.error('Duplicate check failed'); }
      finally { setDupChecking(false); }
    }, 800);
    return () => { clearTimeout(t); setDupChecking(false); };
  }, [data.material_description, data.base_uom]);

  // The Material Number is mandatory for the SAP material types the checker
  // enforces it on — the same rule, read from the shared field config.
  const materialNoRequired = isMaterialNumberRequired(data.material_type);
  const materialNoMissing = materialNoRequired && !data.material_number.trim();

  const setField = (k, v) => setData(prev => {
    const next = { ...prev, [k]: v };
    // The Material Number only applies to certain material types — drop it when
    // the type no longer calls for it.
    if (k === 'material_type' && !isMaterialNumberRequired(v)) next.material_number = '';
    return next;
  });

  const canSubmit = !dupChecking
    && dupResult?.can_create !== false
    && data.material_description.trim()
    && data.material_type
    && data.material_group
    && data.base_uom
    && !materialNoMissing
    && title.trim();

  const handleSubmit = async e => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      await api.post('/material-requests', { title, description, data });
      toast.success('Request submitted successfully!');
      setTitle('');
      setDescription('');
      setData({ ...EMPTY_DATA });
      setDupResult(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submission failed');
    } finally { setLoading(false); }
  };

  const handleBulkSubmit = useCallback(async rows => {
    let ok = 0, fail = 0;
    const failures = [];
    for (const row of rows) {
      try {
        await api.post('/material-requests', {
          title: `Bulk: ${row.material_description}`,
          description: 'Submitted via bulk Excel upload',
          data: row,
        });
        ok++;
      } catch (err) {
        fail++;
        failures.push(`${row.material_description}: ${err.response?.data?.message || 'request failed'}`);
      }
    }
    if (ok) toast.success(`${ok} request(s) submitted successfully`);
    if (fail) toast.error(`${fail} request(s) failed — ${failures.slice(0, 3).join(' | ')}`);
  }, []);

  return (
    <div>
      <div style={{ ...S.banner, background: GRADIENT }}>
        <div style={S.bannerCircle} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={S.bannerTitle}>Material Master Request</div>
          <div style={S.bannerSub}>Submit a new Material Master request or upload in bulk via Excel</div>
        </div>
      </div>

      <div style={S.modeTabs}>
        <button type="button"
          style={{ ...S.modeTab, ...(mode === 'single' ? S.modeTabActive : {}) }}
          onClick={() => setMode('single')}>
          &#x270F; Single Request
        </button>
        <button type="button"
          style={{ ...S.modeTab, ...(mode === 'bulk' ? S.modeTabActive : {}) }}
          onClick={() => setMode('bulk')}>
          &#x1F4CA; Bulk Upload (Excel)
        </button>
      </div>

      {mode === 'single' && (
        <form onSubmit={handleSubmit}>
          <div style={S.card}>
            <div style={S.cardHeader}>Request Information</div>
            <div style={S.field}>
              <label style={S.label}>Request Title <span style={S.req}>*</span></label>
              <input style={S.input} value={title}
                onChange={e => setTitle(e.target.value)}
                required placeholder="e.g. New Material Master — Carbon Black N330" />
            </div>
            <div style={S.field}>
              <label style={S.label}>Description / Justification</label>
              <textarea style={{ ...S.input, height: 80, resize: 'vertical' }}
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Provide additional context or business justification..." />
            </div>
          </div>

          <div style={S.card}>
            <div style={S.cardHeader}>Material Details</div>
            <div style={S.grid}>
              <div style={S.field}>
                <label style={S.label}>Material Type <span style={S.req}>*</span></label>
                <SearchSelect
                  value={data.material_type}
                  options={materialTypes}
                  onChange={v => setField('material_type', v)}
                  placeholder="Search Material Type..." />
              </div>
              <div style={S.field}>
                <label style={S.label}>
                  Material No {materialNoRequired && <span style={S.req}>*</span>}
                </label>
                <input style={S.input}
                  value={data.material_number}
                  onChange={e => setField('material_number', e.target.value)}
                  placeholder={materialNoRequired ? 'Enter Material No' : 'Not mandatory for this material type'}
                  maxLength={40} />
                <div style={materialNoMissing ? S.hintErr : S.hint}>
                  {materialNoRequired
                    ? `Mandatory for material type ${data.material_type}.`
                    : `Mandatory only for ${MATERIAL_NUMBER_TYPES.join(', ')}.`}
                </div>
              </div>
              <div style={S.field}>
                <label style={S.label}>Material Group <span style={S.req}>*</span></label>
                <SearchSelect
                  value={data.material_group}
                  options={materialGroups}
                  onChange={v => setField('material_group', v)}
                  placeholder="Search Material Group..." />
              </div>
              <div style={S.field}>
                <label style={S.label}>Material Description <span style={S.req}>*</span></label>
                <input style={S.input}
                  value={data.material_description}
                  onChange={e => setField('material_description', e.target.value)}
                  placeholder="Enter Material Description"
                  maxLength={40} />
              </div>
              <div style={S.field}>
                <label style={S.label}>Base UOM <span style={S.req}>*</span></label>
                <SearchSelect
                  value={data.base_uom}
                  options={uoms}
                  onChange={v => setField('base_uom', v)}
                  placeholder="Search Unit of Measure..." />
              </div>
            </div>
          </div>

          {data.material_description.trim() && (
            <div style={S.card}>
              <div style={S.cardHeaderRow}>
                <span style={S.cardHeaderInline}>Duplicate Check</span>
                {dupChecking && <span style={S.dupChecking}>Checking&hellip;</span>}
              </div>
              {!dupChecking && dupResult?.status === 'EXACT_DUPLICATE' && (
                <div style={S.dupExact}>
                  <div style={S.dupExactTitle}>&#x274C; Material already exists — creation is blocked</div>
                  {dupResult.matches.map(m => (
                    <div key={m.material} style={{ fontSize: 12.5, color: '#374151', marginBottom: 4 }}>
                      <strong>{m.material}</strong> — {m.description}
                    </div>
                  ))}
                </div>
              )}
              {!dupChecking && dupResult?.status === 'SIMILAR_FOUND' && (
                <div style={S.dupSimilar}>
                  <div style={S.dupSimilarTitle}>&#x26A0; Similar materials found — review before submitting</div>
                  <table style={S.dupTable}>
                    <thead>
                      <tr>
                        {['Material', 'Description', 'Similarity', 'SAP UOM', 'Your UOM', 'UOM Match'].map(h => (
                          <th key={h} style={S.dupTh}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {dupResult.matches.map(m => (
                        <tr key={m.material}>
                          <td style={S.dupTd}>{m.material}</td>
                          <td style={S.dupTd}>{m.description}</td>
                          <td style={{ ...S.dupTd, fontWeight: 700, color: m.similarity >= 95 ? '#dc2626' : '#d97706' }}>{m.similarity}%</td>
                          <td style={S.dupTd}>{m.sap_uom || '—'}</td>
                          <td style={S.dupTd}>{m.user_uom || '—'}</td>
                           <td style={{ ...S.dupTd, fontWeight: 700, color: m.uom_match ? '#4f6ef7' : '#dc2626' }}>{m.uom_match ? '✓' : '✗'}</td>

                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {!dupChecking && dupResult?.status === 'NO_MATCH' && (
                <div style={S.dupOk}>&#x2713; No duplicates found — you may proceed to submit.</div>
              )}
            </div>
          )}

          <div style={S.actions}>
            <button
              style={{ ...S.btn, background: GRADIENT, opacity: (!canSubmit || loading) ? 0.5 : 1 }}
              type="submit"
              disabled={!canSubmit || loading}>
              {loading ? 'Submitting...' : 'Submit Material Request'}
            </button>
          </div>
        </form>
      )}

      {mode === 'bulk' && (
        <div style={S.card}>
          <div style={S.cardHeader}>Bulk Upload via Excel</div>
          <BulkMaterialUpload
            materialGroups={materialGroups}
            uoms={uoms}
            onSubmitBulk={handleBulkSubmit} />
        </div>
      )}
    </div>
  );
};

const S = {
  banner: { borderRadius: 14, padding: '24px 28px', marginBottom: 20, position: 'relative', overflow: 'hidden' },
  bannerCircle: { position: 'absolute', width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', top: -60, right: -40 },
  bannerTitle: { color: '#fff', fontSize: 20, fontWeight: 800, marginBottom: 4 },
  bannerSub: { color: 'rgba(255,255,255,0.7)', fontSize: 13 },
  modeTabs: { display: 'flex', gap: 8, marginBottom: 16 },
  modeTab: { padding: '10px 22px', borderRadius: 10, border: '1.5px solid #c3cbe0', background: '#fff', color: '#4a5568', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' },
  modeTabActive: { background: GRADIENT, color: '#fff', border: 'none', boxShadow: '0 4px 12px rgba(217,90,26,0.3)' },
  card: { background: '#fff', borderRadius: 14, padding: '20px 24px', marginBottom: 16, boxShadow: '0 2px 12px rgba(11,21,84,0.06)' },
  cardHeader: { fontSize: 12, fontWeight: 800, color: '#0B1554', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 18, paddingBottom: 12, borderBottom: '1px solid #f0f2f8' },
  cardHeaderRow: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 14, paddingBottom: 12, borderBottom: '1px solid #f0f2f8' },
  cardHeaderInline: { fontSize: 12, fontWeight: 800, color: '#0B1554', letterSpacing: 0.8, textTransform: 'uppercase' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' },
  field: { marginBottom: 14 },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#4a5568', marginBottom: 5 },
  req: { color: '#DD3222' },
  hint: { fontSize: 11.5, color: '#8892b0', marginTop: 4 },
  hintErr: { fontSize: 11.5, fontWeight: 700, color: '#DD3222', marginTop: 4 },
  input: { width: '100%', padding: '10px 13px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, color: '#0B1554', background: '#f8faff', boxSizing: 'border-box' },
  actions: { display: 'flex', gap: 12, alignItems: 'center' },
  btn: { padding: '12px 32px', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,0,0,0.15)' },
  dupChecking: { fontSize: 12, fontWeight: 700, color: '#4f6ef7', background: '#f0f4ff', padding: '3px 10px', borderRadius: 20 },
  dupExact: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 14px' },
  dupExactTitle: { fontSize: 13, fontWeight: 800, color: '#dc2626', marginBottom: 8 },
  dupSimilar: { background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 14px' },
  dupSimilarTitle: { fontSize: 13, fontWeight: 800, color: '#b45309', marginBottom: 10 },
  dupTable: { width: '100%', borderCollapse: 'collapse', fontSize: 12.5 },
  dupTh: { textAlign: 'left', padding: '6px 10px', background: '#fef3c7', color: '#92400e', fontWeight: 700, fontSize: 11, borderBottom: '1px solid #fde68a' },
  dupTd: { padding: '7px 10px', borderBottom: '1px solid #fef3c7', color: '#374151' },
  dupOk: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 14px', fontSize: 13, fontWeight: 700, color: '#15803d' },
};

export default MaterialRequestForm;
